import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { useUserProfile, useOnboardingStatus } from '@/hooks/api/use-onboarding';
import { usePermissions } from '@/hooks/api/use-permissions';
import { useTeamMembers } from '@/hooks/api/use-team';
import { OnboardingChecklist } from '@/components/dashboard/onboarding-checklist';
import { TemplateCards } from '@/components/dashboard/template-cards';
import { TeamCard } from '@/components/dashboard/team-card';
import { LawyerDashboard } from '@/components/dashboard/lawyer-dashboard';

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
});

function DashboardSkeleton() {
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header skeleton */}
      <div className="h-8 w-64 animate-pulse rounded-md bg-muted" />

      {/* Checklist skeleton */}
      <div className="h-48 animate-pulse rounded-xl border bg-card" />

      {/* Templates skeleton */}
      <div className="space-y-3">
        <div className="h-5 w-40 animate-pulse rounded bg-muted" />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="h-24 animate-pulse rounded-xl border bg-card" />
          <div className="h-24 animate-pulse rounded-xl border bg-card" />
        </div>
      </div>
    </div>
  );
}

function DashboardPage() {
  const { t } = useTranslation();
  const { data: user, isPending: userPending } = useUserProfile();
  const { data: onboarding } = useOnboardingStatus();
  const { data: permissions, isPending: permissionsPending } = usePermissions();

  // Show toast when redirected from /team due to insufficient permissions
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('teamDenied') === 'true') {
      toast.error(t('team.noAccessRedirect'));
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [t]);

  const role = permissions?.role;
  const isLawyer = role === 'LAWYER';
  const canManageTeam = role === 'OWNER' || role === 'ADMIN';

  // Only fetch team members for Owner/Admin (endpoint requires OWNER/ADMIN role)
  const { data: members } = useTeamMembers({ status: 'active' }, { enabled: canManageTeam });
  const memberCount = members?.total ?? 1;

  // Show skeleton while critical data loads (user profile + permissions determine layout)
  if (userPending || permissionsPending) {
    return <DashboardSkeleton />;
  }

  const displayName = user?.name || t('user.anonymous');

  // Lawyer gets dedicated full-width dashboard
  if (isLawyer) {
    return <LawyerDashboard userName={displayName} />;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header */}
      <h1 className="text-2xl font-bold tracking-tight">
        {t('dashboard.greeting', { name: displayName })}
      </h1>

      {/* Onboarding checklist */}
      {onboarding && (
        <OnboardingChecklist
          profileCompleted={onboarding.profileCompleted}
          workspaceCompleted={onboarding.workspaceCompleted}
          hasMembers={memberCount > 1}
          canManageTeam={canManageTeam}
          contractCreated={onboarding.contractCreated}
        />
      )}

      {/* Contract templates */}
      <TemplateCards />

      {/* Team card — Owner/Admin only */}
      {canManageTeam && <TeamCard memberCount={memberCount} />}
    </div>
  );
}
